import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';
import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import TabPageContainer from '~components/atoms/TabPageContainer';

import AdminsSearchAndFilterBar from './AdminsSearchAndFilterBar';
import AdminsTable from './AdminsTable';
import AdminsPaginator from './AdminsPaginator';
import AdminsCreateAdminModal from './AdminsCreateAdminModal';
import AdminsAdminDetailsModal from './AdminsAdminDetailsModal';
import AdminsDeleteAdminModal from './AdminsDeleteAdminModal';

import { ExportPermissionsEnum, ModuleEnum, PermissionsEnum, RoleStatusEnum } from '~utils/enums';
import * as Styles from './_admins.module.scss';
import UtilFunctions from '~utils/functions';
import DownloadCSV from '~components/molecules/DownloadCSV';
import { TExportAdminUserPermission } from '~types/feat/admin';
import dayjs from 'dayjs';

const Admins = () => {
    const dispatch = useAppDispatch();

    const { Misc: { dateTimeFormat } } = UtilFunctions;

    const handleCreateNewAdminClick = () => dispatch(combinedActions.adminSetCreateNewAdminModalIsOpen(true));
    const exportAdminUserListing = () => dispatch(combinedActions.adminExportAdminUserListingAttempt());
    const clearAdminUserListingForExport = () => dispatch(combinedActions.adminClearAdminUserListingForExport());

    const [isExportingAdminUserListing, setIsExportingAdminUserListing] = useState(false);
    const modules = Object.values(ModuleEnum).filter(module => Number.isNaN(Number(module))) as string[];
    const permissionValues = Object.values(ExportPermissionsEnum).filter(module => Number.isNaN(Number(module))) as string[];

    const getAdminUserListingForExportIsLoading = useAppSelector(state => state.admin.loadings.exportAdminUserListing);
    const createAccessModules = useAppSelector(state => state.auth.userDetails.access)?.find(access => access.permission === PermissionsEnum.Create)?.modules || [];
    const adminUserListingForExport = useAppSelector(state => state.admin.adminUserListingForExport);

    useEffect(() => {
        dispatch(combinedActions.branchGetAllBranchesAttempt());
    }, []);

    const handleExportInventoryTransactionListingCleanup = () => {
        clearAdminUserListingForExport();
        setIsExportingAdminUserListing(false);
    };

    const permissionHeaders = modules.flatMap(module => {
        const spacedModule = module.split(/(?=[A-Z])/).join(' ').toUpperCase();

        return [
            `${spacedModule} VIEW`,
            `${spacedModule} CREATE`,
            `${spacedModule} EDIT`,
        ];
    });

    const listHeader = [
        'USER ID', 'STATUS', 'SAM ACCOUNT NAME', 'DISPLAY NAME', 'BRANCH ID',
        'ROLE NAME', 'EMAIL', 'REFERRAL CODE', 'CREATED AT', 'UPDATED AT',
        'LAST LOGIN AT', 'ROLE NAME',
        ...permissionHeaders,
    ];

    const listData = adminUserListingForExport.map(user => {
        const {
            userId,
            status,
            sAMAccountName,
            displayName,
            branchId,
            email,
            referralCode,
            createdAt,
            updatedAt,
            lastLoginAt,
            roleName,
            permissions,
        } = user;

        const renderPermissionEnum = (permissions: TExportAdminUserPermission[]) => {
            return modules.map((module) => {
                const filteredModule = permissions.filter(item => item.moduleName === (module === 'Buy & Sell' ? 'BuySell' : module.replace(/ /g, '')));

                const cleanedList = filteredModule.filter((obj, index, self) =>
                    index === self.findIndex((o) => o.moduleName === obj.moduleName && o.permission === obj.permission)
                ).sort((a, b) => a.permission - b.permission);

                return permissionValues.map((value) => {
                    const filteredPermission = cleanedList.find(item => ExportPermissionsEnum[item.permission] === value);

                    return filteredPermission ? 'X' : 'B';
                });
            });

        };

        return {
            userId: userId || '-',
            status: RoleStatusEnum[status] || '-',
            sAMAccountName: sAMAccountName || '-',
            displayName: displayName || '-',
            branchId: branchId || '-',
            roleName: roleName || '-',
            email: email || '-',
            referralCode: referralCode || '-',
            createdAt: dateTimeFormat(createdAt) || '-',
            updatedAt: dateTimeFormat(updatedAt) || '-',
            lastLoginAt: dateTimeFormat(lastLoginAt) || '-',
            permission: renderPermissionEnum(permissions) || '-',
        };
    });

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Admin List'
                    className={Styles.title}
                    size='l'
                    isBold
                />

                <div className={Styles['btn-container']}>
                    {createAccessModules.find(module => module === ModuleEnum.RBAC)
                        && (
                            <DownloadCSV
                                buttonLabel='Export Admins List to CSV'
                                header={listHeader}
                                data={listData}
                                fileName={dayjs().format('DD-MM-YYYY').concat(' Admin User Listing')}
                                onClick={exportAdminUserListing}
                                cleanup={handleExportInventoryTransactionListingCleanup}
                                isLoading={getAdminUserListingForExportIsLoading || isExportingAdminUserListing}
                            />
                        )
                    }

                    {createAccessModules.find(module => module === ModuleEnum.RBAC)
                        && (
                            <ActionButton
                                label='+ Create New User'
                                onClick={handleCreateNewAdminClick}
                            />
                        )
                    }
                </div>

            </TabPageHeaderWrapper>

            <AdminsSearchAndFilterBar />

            <AdminsTable />

            <AdminsPaginator />

            <>
                <AdminsCreateAdminModal />

                <AdminsAdminDetailsModal />

                <AdminsDeleteAdminModal />
            </>
        </TabPageContainer>
    );
};

export default Admins;
